import { useQuery } from '@tanstack/react-query';

import { CHAIN_IDS } from '@/constants/chain';

import { fetchJsonOrThrow } from '@/utils/fetch';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';

export interface WalletChartPoint {
  timestamp: number;
  value: number;
}

interface WalletChartResponse {
  points: WalletChartPoint[];
}

type ChartPeriod = 'hour' | 'day' | 'week' | 'month' | 'year' | 'max';

interface UseWalletChartParams {
  evmAddress?: string;
  solanaAddress?: string;
  period?: ChartPeriod;
  enabled?: boolean;
  chainId?: number | 'all';
}

const fetchChart = (url: string) => fetchJsonOrThrow<WalletChartResponse>(url);

function buildUrl({
  evmAddress,
  solanaAddress,
  period,
  chainId,
}: {
  evmAddress?: string;
  solanaAddress?: string;
  period: ChartPeriod;
  chainId?: number | 'all';
}): string | null {
  // Skip an address if the active chain filter excludes it.
  const includeEvm = chainId === 'all' || chainId === undefined || chainId !== CHAIN_IDS.SOLANA;
  const includeSolana = chainId === 'all' || chainId === undefined || chainId === CHAIN_IDS.SOLANA;

  const effectiveEvm = includeEvm ? evmAddress : undefined;
  const effectiveSolana = includeSolana ? solanaAddress : undefined;
  if (!effectiveEvm && !effectiveSolana) return null;

  const params = new URLSearchParams();
  if (effectiveEvm) params.set('evmAddress', effectiveEvm);
  if (effectiveSolana) params.set('solanaAddress', effectiveSolana);
  params.set('period', period);
  if (chainId && chainId !== 'all') params.set('chains', String(chainId));
  return `${routes.api.WALLET_CHART}?${params.toString()}`;
}

export const walletChartQueryKey = (params: {
  evmAddress?: string;
  solanaAddress?: string;
  period: ChartPeriod;
  chainId?: number | 'all';
}) => ['wallet-chart', params] as const;

export function useWalletChart({
  evmAddress,
  solanaAddress,
  period = 'week',
  enabled = true,
  chainId,
}: UseWalletChartParams) {
  const url = buildUrl({ evmAddress, solanaAddress, period, chainId });

  const { data, ...rest } = useQuery<WalletChartResponse, Error>({
    queryKey: walletChartQueryKey({ evmAddress, solanaAddress, period, chainId }),
    queryFn: () => fetchChart(url!),
    enabled: enabled && url !== null,
    staleTime: 300_000,
    retry: retryNetworkOrServerError,
  });

  return {
    chart: data?.points ?? [],
    ...rest,
  };
}
